import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col } from 'react-bootstrap';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';

import { RootState } from '../../../ducks/store';
import { getOnlineData } from '../../../ducks/presence';
import { Table } from '../../common/Table/Table';
import PageHeader from '../../common/PageHeader/PageHeader';

import { Button } from '@praktio/common-ui';
import { toast } from 'react-toastify';
import { useQuery } from '../../../utils/functions';
import { forceMaintenance } from '../../../utils/api';

import './Online.scss';

const PRESENCE_STATE = Object.freeze({ active: 'ACTIVE', idle: 'IDLE' });

const StatusCell = ({ row }: { value: string; row: { original: { presenceState: string } } }) => {
  const isActive = row.original.presenceState === PRESENCE_STATE.active;
  return (
    <span className={`Online-status ${isActive ? 'Online-status--active' : 'Online-status--idle'}`}>
      {isActive ? 'Active' : 'Idle'}
    </span>
  );
};

const UrlCell = ({ value }: { value: string }) => {
  // The Table's Cell type is `JSX.Element | undefined` (not `| null`), so an
  // empty row renders nothing via `undefined` rather than `null`.
  if (!value) return undefined;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard.');
    } catch (e) {
      console.error(e);
      toast.error('Unable to copy. Please try again.');
    }
  };
  return (
    <span className="Online-url">
      <span className="Online-url-text" title={value}>
        {value}
      </span>
      <button type="button" className="Online-url-copy" onClick={handleCopy} aria-label="Copy URL">
        <span className="material-symbols-sharp">content_copy</span>
      </button>
    </span>
  );
};

// The shared Table default-sorts by its first column, so Email is placed first
// to sort the page by email.
const UserHeaders = [
  { Header: 'Email', accessor: 'email', isId: true },
  { Header: 'Activity', accessor: 'context', className: 'Online-activity' },
  { Header: 'URL', accessor: 'url', Cell: UrlCell },
  { Header: 'Status', accessor: 'statusLabel', Cell: StatusCell },
  { Header: 'Name', accessor: 'name' },
  { Header: 'Last seen', accessor: 'lastSeenLabel' },
];

const Online = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const force = query.get('force') === '1';
  const onlineData = useSelector((state: RootState) => state.presence.onlineData);

  const getData = () => {
    dispatch(getOnlineData() as any);
  };

  // WS pushes keep this fresh; fetch once on mount for the initial paint and on
  // manual REFRESH.
  useEffect(() => {
    getData();
  }, [dispatch]);

  const uniqueUsers = new Set(onlineData.map((u) => u.userId)).size;
  const activeTabs = onlineData.filter((u) => u.state === PRESENCE_STATE.active).length;
  const idleTabs = onlineData.length - activeTabs;

  const rows = onlineData.map((u) => ({
    presenceState: u.state,
    statusLabel: u.state,
    context: u.context ?? '',
    url: u.url ?? '',
    email: u.email ?? '',
    name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
    lastSeenLabel: new Date(u.lastSeenAt).toLocaleTimeString(),
  }));

  const countsLabel = `${uniqueUsers} users online · ${onlineData.length} tabs · ${activeTabs} active · ${idleTabs} idle`;

  return (
    <Container fluid className="Online">
      <Helmet>
        <title>Online - Praktio</title>
      </Helmet>
      <Row className="h-100">
        <Col className={classNames('p-0 h-100', 'd-flex flex-column col-xs-12')}>
          <PageHeader pageName="Users Online">
            <span className="Online-counts">{countsLabel}</span>
            <Button className="secondary" onClick={() => getData()}>
              REFRESH
            </Button>
            {force ? (
              <div className="maintenance-row">
                <Button className="secondary" onClick={() => forceMaintenance()}>
                  FORCE
                </Button>
              </div>
            ) : null}
          </PageHeader>
          <div className="Online-content">
            <Table columns={UserHeaders} data={rows} />
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Online;
